import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { IJukeboxItem, Ijukebox } from '@typings/setting/jukebox.interface';
import Icon from '@components/dataDisplay/Icons/Icon';
import { useRecoilState, useRecoilValue } from 'recoil';
import { muteState, playState, volumeState } from '@atom/setting/jukebox';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
`;

const SlidePanel = styled(motion.div)<{
  $bgColor?: string;
  $textColor?: string;
}>`
  position: fixed;
  top: 0;
  right: 0;
  width: 360px;
  max-width: 90vw;
  height: 100vh;
  background: ${({ $bgColor }) => $bgColor || 'var(--backgroundColor)'};
  color: ${({ $textColor }) => $textColor || 'var(--textColor)'};
  z-index: 9999;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);

  @media (max-width: 480px) {
    width: 100vw;
  }
`;

const PanelHeader = styled.div<{ $lineColor?: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid
    ${({ $lineColor }) => $lineColor || 'rgba(255, 255, 255, 0.1)'};
`;

const PanelTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;

const NowPlayingSection = styled.div<{
  $cardColor?: string;
  $lineColor?: string;
}>`
  padding: 20px;
  background: ${({ $cardColor }) => $cardColor || 'rgba(0, 0, 0, 0.2)'};
  border-bottom: 1px solid
    ${({ $lineColor }) => $lineColor || 'rgba(255, 255, 255, 0.1)'};
`;

const NowPlayingLabel = styled.div<{ $subTextColor?: string }>`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.8;
  margin-bottom: 12px;
  color: ${({ $subTextColor }) => $subTextColor || 'inherit'};
`;

const NowPlayingCard = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const AlbumArt = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const AlbumArtPlaceholder = styled.div<{ $buttonColor?: string }>`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  background: ${({ $buttonColor }) =>
    $buttonColor || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const NowPlayingInfo = styled.div`
  flex: 1;
  overflow: hidden;
`;

const TrackTitle = styled.div<{ $textColor?: string }>`
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
  color: ${({ $textColor }) => $textColor || 'inherit'};
`;

const TrackArtist = styled.div<{ $subTextColor?: string }>`
  font-size: 13px;
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $subTextColor }) => $subTextColor || 'inherit'};
`;

const PlayingIndicator = styled.div<{ $buttonColor?: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 11px;
  color: ${({ $buttonColor }) =>
    $buttonColor || 'var(--primaryColor, #667eea)'};
`;

const PlaylistSection = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 0;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
`;

const PlaylistHeader = styled.div<{ $subTextColor?: string }>`
  padding: 0 20px 12px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.8;
  color: ${({ $subTextColor }) => $subTextColor || 'inherit'};
`;

const PlaylistItem = styled.div<{
  $isPlaying?: boolean;
  $buttonColor?: string;
  $cardColor?: string;
}>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  cursor: pointer;
  transition: background 0.2s;
  border-left: 3px solid
    ${({ $isPlaying, $buttonColor }) =>
      $isPlaying
        ? $buttonColor || 'var(--primaryColor, #667eea)'
        : 'transparent'};
  background: ${({ $isPlaying, $cardColor }) =>
    $isPlaying
      ? $cardColor
        ? `${$cardColor}66`
        : 'rgba(255, 255, 255, 0.05)'
      : 'transparent'};

  &:hover {
    background: ${({ $cardColor }) =>
      $cardColor ? `${$cardColor}44` : 'rgba(255, 255, 255, 0.1)'};
  }
`;

const ItemNumber = styled.div<{ $isPlaying?: boolean; $buttonColor?: string }>`
  width: 24px;
  font-size: 13px;
  opacity: ${({ $isPlaying }) => ($isPlaying ? 1 : 0.5)};
  color: ${({ $isPlaying, $buttonColor }) =>
    $isPlaying ? $buttonColor || 'var(--primaryColor, #667eea)' : 'inherit'};
`;

const ItemThumbnail = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 4px;
  object-fit: cover;
`;

const ItemThumbnailPlaceholder = styled.div<{ $lineColor?: string }>`
  width: 44px;
  height: 44px;
  border-radius: 4px;
  background: ${({ $lineColor }) =>
    $lineColor || 'linear-gradient(135deg, #374151 0%, #1f2937 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ItemInfo = styled.div`
  flex: 1;
  overflow: hidden;
`;

const ItemTitle = styled.div<{ $isPlaying?: boolean; $buttonColor?: string }>`
  font-size: 14px;
  font-weight: ${({ $isPlaying }) => ($isPlaying ? 600 : 400)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 4px;
  color: ${({ $isPlaying, $buttonColor }) =>
    $isPlaying ? $buttonColor || 'var(--primaryColor, #667eea)' : 'inherit'};
`;

const ItemArtist = styled.div<{ $subTextColor?: string }>`
  font-size: 12px;
  opacity: 0.6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $subTextColor }) => $subTextColor || 'inherit'};
`;

const ControlsSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 12px;
`;

const VolumeWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const VolumePopover = styled.div<{ $bgColor?: string; $lineColor?: string }>`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: ${({ $bgColor }) => $bgColor || 'rgba(30, 30, 30, 0.95)'};
  border: 1px solid ${({ $lineColor }) => $lineColor || 'rgba(255,255,255,0.12)'};
  border-radius: 8px;
  padding: 10px 14px;
  width: 140px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  z-index: 10;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 16px rgba(0,0,0,0.3);
`;

const VolumeLabel = styled.div<{ $subTextColor?: string }>`
  font-size: 10px;
  opacity: 0.7;
  text-align: center;
  color: ${({ $subTextColor }) => $subTextColor || 'inherit'};
`;

const VolumeSlider = styled.input<{ $buttonColor?: string; $lineColor?: string }>`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 4px;
  border-radius: 2px;
  outline: none;
  cursor: pointer;
  background: linear-gradient(
    to right,
    ${({ $buttonColor }) => $buttonColor || 'var(--primaryColor, #667eea)'} var(--vol),
    ${({ $lineColor }) => $lineColor || 'rgba(255,255,255,0.2)'} var(--vol)
  );

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${({ $buttonColor }) => $buttonColor || 'var(--primaryColor, #667eea)'};
    cursor: pointer;
    box-shadow: 0 0 4px rgba(0,0,0,0.3);
  }

  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: none;
    background: ${({ $buttonColor }) => $buttonColor || 'var(--primaryColor, #667eea)'};
    cursor: pointer;
  }
`;

const ControlButton = styled.button<{
  $size?: 'sm' | 'md' | 'lg';
  $buttonColor?: string;
}>`
  background: ${({ $buttonColor }) =>
    $buttonColor ? `${$buttonColor}33` : 'rgba(255, 255, 255, 0.1)'};
  border: none;
  border-radius: 50%;
  width: ${({ $size }) =>
    $size === 'lg' ? '48px' : $size === 'sm' ? '32px' : '40px'};
  height: ${({ $size }) =>
    $size === 'lg' ? '48px' : $size === 'sm' ? '32px' : '40px'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  color: inherit;

  &:hover {
    background: ${({ $buttonColor }) =>
      $buttonColor ? `${$buttonColor}66` : 'rgba(255, 255, 255, 0.2)'};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ProgressBarWrapper = styled.div`
  width: 100%;
  padding: 8px 0;
  cursor: pointer;
  position: relative;
`;

const ProgressBar = styled.div<{ $lineColor?: string }>`
  width: 100%;
  height: 4px;
  background: ${({ $lineColor }) => $lineColor || 'rgba(255, 255, 255, 0.2)'};
  border-radius: 2px;
  position: relative;
`;

const Progress = styled.div<{
  $progress: number;
  $buttonColor?: string;
  $isDragging: boolean;
}>`
  height: 100%;
  width: ${({ $progress }) => $progress}%;
  background: ${({ $buttonColor }) =>
    $buttonColor || 'var(--primaryColor, #667eea)'};
  border-radius: 2px;
  transition: ${({ $isDragging }) =>
    $isDragging ? 'none' : 'width 0.1s linear'};
`;

const ProgressThumb = styled.div<{
  $progress: number;
  $buttonColor?: string;
  $isDragging: boolean;
}>`
  position: absolute;
  top: 50%;
  left: ${({ $progress }) => $progress}%;
  transform: translate(-50%, -50%);
  width: ${({ $isDragging }) => ($isDragging ? '14px' : '12px')};
  height: ${({ $isDragging }) => ($isDragging ? '14px' : '12px')};
  border-radius: 50%;
  background: ${({ $buttonColor }) =>
    $buttonColor || 'var(--primaryColor, #667eea)'};
  box-shadow: 0 0 4px rgba(0, 0, 0, 0.3);
  cursor: grab;
  transition: width 0.15s, height 0.15s;
  pointer-events: none;
`;

const TimeInfo = styled.div<{ $subTextColor?: string }>`
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  opacity: 0.6;
  color: ${({ $subTextColor }) => $subTextColor || 'inherit'};
`;

const LyricsSection = styled.div<{ $lineColor?: string }>`
  padding: 16px 20px;
  border-top: 1px solid var(--lineColor);
  max-height: 200px;
  overflow-y: auto;
`;

const LyricsTitle = styled.div<{ $subTextColor?: string }>`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.8;
  margin-bottom: 8px;
  color: ${({ $subTextColor }) => $subTextColor || 'inherit'};
`;

const LyricsText = styled.pre`
  font-size: 13px;
  line-height: 1.6;
  white-space: pre-wrap;
  margin: 0;
  opacity: 0.8;
  font-family: inherit;
`;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  playlist: IJukeboxItem[];
  currentIndex: number;
  setCurrentIndex: (index: number) => void;
  jukeboxData: Ijukebox | null | undefined;
  progress?: number;
  currentTime?: number;
  duration?: number;
  onSeekRequest?: (time: number) => void;
};

function PlaylistSlidePanel({
  isOpen,
  onClose,
  playlist,
  currentIndex,
  setCurrentIndex,
  jukeboxData,
  progress = 0,
  currentTime = 0,
  duration = 0,
  onSeekRequest,
}: Props) {
  const [isPlaying, setIsPlaying] = useRecoilState(playState);
  const [muted, setMuted] = useRecoilState(muteState);
  const [volume, setVolume] = useRecoilState(volumeState);
  const currentTrack = playlist?.[currentIndex];

  // 패널 내 프로그레스바 드래그 상태
  const [isDragging, setIsDragging] = useState(false);
  const [dragProgress, setDragProgress] = useState(0);
  const [dragCurrentTime, setDragCurrentTime] = useState(0);
  const [isVolumeOpen, setIsVolumeOpen] = useState(false);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const volumeWrapperRef = useRef<HTMLDivElement>(null);

  const displayProgress = isDragging ? dragProgress : progress;
  const displayCurrentTime = isDragging ? dragCurrentTime : currentTime;

  const getSourceType = useCallback(
    (track: IJukeboxItem): 'youtube' | 'file' | null => {
      if (track?.file?.url) return 'file';
      if (track?.youtubeURL) return 'youtube';
      return null;
    },
    [],
  );

  const handleItemClick = useCallback(
    (index: number) => {
      setCurrentIndex(index);
    },
    [setCurrentIndex],
  );

  const handlePlayPause = useCallback(() => {
    if (!isPlaying && muted) {
      setMuted(false);
    }
    setIsPlaying((prev) => !prev);
  }, [isPlaying, muted, setIsPlaying, setMuted]);

  const handleVolumeIconClick = useCallback(() => {
    setIsVolumeOpen((prev) => !prev);
  }, []);

  const handleToggleMute = useCallback(() => {
    setMuted(!muted);
  }, [muted, setMuted]);

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value);
      setVolume(val);
      if (muted && val > 0) setMuted(false);
    },
    [muted, setMuted, setVolume],
  );

  useEffect(() => {
    if (!isVolumeOpen) return;
    const handleOutside = (e: MouseEvent) => {
      if (volumeWrapperRef.current && !volumeWrapperRef.current.contains(e.target as Node)) {
        setIsVolumeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isVolumeOpen]);

  const handlePrev = useCallback(() => {
    if (!playlist || playlist.length === 0) return;
    setCurrentIndex(
      currentIndex === 0 ? playlist.length - 1 : currentIndex - 1,
    );
    if (!isPlaying) setIsPlaying(true);
    if (muted) setMuted(false);
  }, [
    currentIndex,
    playlist,
    setCurrentIndex,
    isPlaying,
    setIsPlaying,
    muted,
    setMuted,
  ]);

  const handleNext = useCallback(() => {
    if (!playlist || playlist.length === 0) return;
    setCurrentIndex((currentIndex + 1) % playlist.length);
    if (!isPlaying) setIsPlaying(true);
    if (muted) setMuted(false);
  }, [
    currentIndex,
    playlist,
    setCurrentIndex,
    isPlaying,
    setIsPlaying,
    muted,
    setMuted,
  ]);

  const formatTime = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s < 10 ? '0' + s : s}`;
  };

  const calcSeekFromEvent = useCallback(
    (clientX: number): { newProgress: number; newTime: number } | null => {
      const bar = progressBarRef.current;
      if (!bar || !duration) return null;
      const rect = bar.getBoundingClientRect();
      const ratio = Math.min(
        Math.max((clientX - rect.left) / rect.width, 0),
        1,
      );
      return { newProgress: ratio * 100, newTime: ratio * duration };
    },
    [duration],
  );

  const handleSeekStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(true);
      const result = calcSeekFromEvent(e.clientX);
      if (result) {
        setDragProgress(result.newProgress);
        setDragCurrentTime(result.newTime);
      }
    },
    [calcSeekFromEvent],
  );

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const result = calcSeekFromEvent(e.clientX);
      if (result) {
        setDragProgress(result.newProgress);
        setDragCurrentTime(result.newTime);
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      setIsDragging(false);
      const result = calcSeekFromEvent(e.clientX);
      if (result) {
        onSeekRequest?.(result.newTime);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, calcSeekFromEvent, onSeekRequest]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <SlidePanel
            $bgColor={jukeboxData?.themeSet?.backgroundColor}
            $textColor={jukeboxData?.themeSet?.textColor}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <PanelHeader $lineColor={jukeboxData?.themeSet?.lineColor}>
              <PanelTitle>재생 목록</PanelTitle>
              <CloseButton onClick={onClose}>
                <Icon name="X14" />
              </CloseButton>
            </PanelHeader>

            {/* 현재 재생 중인 트랙 */}
            {currentTrack && (
              <NowPlayingSection
                $cardColor={jukeboxData?.themeSet?.cardColor}
                $lineColor={jukeboxData?.themeSet?.lineColor}
              >
                <NowPlayingLabel
                  $subTextColor={jukeboxData?.themeSet?.subTextColor}
                >
                  Now Playing
                </NowPlayingLabel>
                <NowPlayingCard>
                  {currentTrack.imgURL ? (
                    <AlbumArt
                      src={currentTrack.imgURL}
                      alt={currentTrack.title}
                    />
                  ) : (
                    <AlbumArtPlaceholder
                      $buttonColor={jukeboxData?.themeSet?.buttonColor}
                    >
                      <Icon name="Play20" />
                    </AlbumArtPlaceholder>
                  )}
                  <NowPlayingInfo>
                    <TrackTitle $textColor={jukeboxData?.themeSet?.textColor}>
                      {currentTrack.title}
                    </TrackTitle>
                    <TrackArtist
                      $subTextColor={jukeboxData?.themeSet?.subTextColor}
                    >
                      {currentTrack.artist || '알 수 없는 아티스트'}
                    </TrackArtist>
                  </NowPlayingInfo>
                </NowPlayingCard>

                {/* 프로그레스바 */}
                <div>
                  <ProgressBarWrapper
                    onMouseDown={handleSeekStart}
                    ref={progressBarRef}
                  >
                    <ProgressBar $lineColor={jukeboxData?.themeSet?.lineColor}>
                      <Progress
                        $progress={displayProgress}
                        $buttonColor={jukeboxData?.themeSet?.buttonColor}
                        $isDragging={isDragging}
                      />
                      <ProgressThumb
                        $progress={displayProgress}
                        $buttonColor={jukeboxData?.themeSet?.buttonColor}
                        $isDragging={isDragging}
                      />
                    </ProgressBar>
                  </ProgressBarWrapper>
                  <TimeInfo $subTextColor={jukeboxData?.themeSet?.subTextColor}>
                    <span>{formatTime(displayCurrentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </TimeInfo>
                </div>

                {/* 컨트롤 버튼 */}
                <ControlsSection>
                  {/* 볼륨 컨트롤 */}
                  <VolumeWrapper ref={volumeWrapperRef}>
                    <ControlButton
                      $size="sm"
                      $buttonColor={jukeboxData?.themeSet?.buttonColor}
                      onClick={handleVolumeIconClick}
                    >
                      <Icon name={muted ? 'VolumeOff18' : 'VolumeOn18'} />
                    </ControlButton>
                    {isVolumeOpen && (
                      <VolumePopover
                        $bgColor={jukeboxData?.themeSet?.backgroundColor}
                        $lineColor={jukeboxData?.themeSet?.lineColor}
                      >
                        <VolumeLabel $subTextColor={jukeboxData?.themeSet?.subTextColor}>
                          {muted ? '음소거' : `${Math.round(volume * 100)}%`}
                        </VolumeLabel>
                        <VolumeSlider
                          type="range"
                          min={0}
                          max={1}
                          step={0.01}
                          value={muted ? 0 : volume}
                          onChange={handleVolumeChange}
                          $buttonColor={jukeboxData?.themeSet?.buttonColor}
                          $lineColor={jukeboxData?.themeSet?.lineColor}
                          style={{ '--vol': `${muted ? 0 : volume * 100}%` } as React.CSSProperties}
                        />
                        <ControlButton
                          $size="sm"
                          $buttonColor={jukeboxData?.themeSet?.buttonColor}
                          onClick={handleToggleMute}
                          style={{ alignSelf: 'center', marginTop: 2 }}
                        >
                          <Icon name={muted ? 'VolumeOff18' : 'VolumeOn18'} />
                        </ControlButton>
                      </VolumePopover>
                    )}
                  </VolumeWrapper>
                  <ControlButton
                    $size="sm"
                    $buttonColor={jukeboxData?.themeSet?.buttonColor}
                    onClick={handlePrev}
                  >
                    <Icon name="PlayPrev14" />
                  </ControlButton>
                  <ControlButton
                    $size="lg"
                    $buttonColor={jukeboxData?.themeSet?.buttonColor}
                    onClick={handlePlayPause}
                  >
                    <Icon name={isPlaying ? 'Pause20' : 'Play20'} />
                  </ControlButton>
                  <ControlButton
                    $size="sm"
                    $buttonColor={jukeboxData?.themeSet?.buttonColor}
                    onClick={handleNext}
                  >
                    <Icon name="PlayNext14" />
                  </ControlButton>
                </ControlsSection>
              </NowPlayingSection>
            )}

            {/* 가사 섹션 */}
            {currentTrack?.lyrics && (
              <LyricsSection $lineColor={jukeboxData?.themeSet?.lineColor}>
                <LyricsTitle
                  $subTextColor={jukeboxData?.themeSet?.subTextColor}
                >
                  가사
                </LyricsTitle>
                <LyricsText>{currentTrack.lyrics}</LyricsText>
              </LyricsSection>
            )}

            {/* 플레이리스트 */}
            <PlaylistSection>
              <PlaylistHeader
                $subTextColor={jukeboxData?.themeSet?.subTextColor}
              >
                총 {playlist?.length || 0}곡
              </PlaylistHeader>
              {playlist?.map((track, index) => {
                const sourceType = getSourceType(track);
                const isCurrent = index === currentIndex;

                return (
                  <PlaylistItem
                    key={track.id || index}
                    $isPlaying={isCurrent}
                    $buttonColor={jukeboxData?.themeSet?.buttonColor}
                    $cardColor={jukeboxData?.themeSet?.cardColor}
                    onClick={() => handleItemClick(index)}
                  >
                    <ItemNumber
                      $isPlaying={isCurrent}
                      $buttonColor={jukeboxData?.themeSet?.buttonColor}
                    >
                      {isCurrent && isPlaying ? (
                        <Icon name="Play20" />
                      ) : (
                        index + 1
                      )}
                    </ItemNumber>
                    {track.imgURL ? (
                      <ItemThumbnail src={track.imgURL} alt={track.title} />
                    ) : (
                      <ItemThumbnailPlaceholder
                        $lineColor={jukeboxData?.themeSet?.lineColor}
                      >
                        <Icon name="Play20" />
                      </ItemThumbnailPlaceholder>
                    )}
                    <ItemInfo>
                      <ItemTitle
                        $isPlaying={isCurrent}
                        $buttonColor={jukeboxData?.themeSet?.buttonColor}
                      >
                        {track.title}
                      </ItemTitle>
                      <ItemArtist
                        $subTextColor={jukeboxData?.themeSet?.subTextColor}
                      >
                        {track.artist || '알 수 없는 아티스트'}
                      </ItemArtist>
                    </ItemInfo>
                    {/* {sourceType && <SourceBadge $type={sourceType}>{sourceType === 'youtube' ? 'YT' : 'MP3'}</SourceBadge>} */}
                  </PlaylistItem>
                );
              })}
            </PlaylistSection>
          </SlidePanel>
        </>
      )}
    </AnimatePresence>
  );
}

export default PlaylistSlidePanel;
